/**
 * DEFUSE S15 — typed classification of the string-bag SIDE-CHANNELS.
 *
 * The 9 documented JSON side-channels (§3.1) are typed objects smuggled through
 * the `EntityPreview.properties` string bag. Classifying them here (with the
 * canonical `DrawingEntity` field each one encodes) gives S14's terminal
 * deletion a BY-CONSTRUCTION roster: S14 stops emitting exactly the
 * `s14Deletable` set once S15 (write) + S16 (panel-read) consume the typed home
 * directly. Reader-only channels + Appendix-A orphans are classified too so the
 * enumeration is exhaustive (no channel silently survives the cutover).
 *
 * This is NOT a panel-property roster — side-channels never render as editable
 * rows; they are the encoding layer the typed descriptors REPLACE.
 */

import type { EntityType } from "@dxf-ai-editor/contracts";

export type SideChannelKind =
  /** a typed object serialized into the bag (the 9 of §3.1). */
  | "typed-object"
  /** read-but-not-produced today (needs an OBJECTS producer). */
  | "reader-only"
  /** produced-but-not-read / dead key (Appendix A). */
  | "orphan";

export type SideChannelDescriptor = {
  /** the `__`-prefixed bag key (or the orphan label). */
  key: string;
  kind: SideChannelKind;
  /** canonical `DrawingEntity` field the channel encodes ("" for dead orphans). */
  typedHome: string;
  /** entity kinds that carry it. */
  carriers: readonly EntityType[];
  /** S14 stops emitting it once S15/S16 consume the typed home. `false` for
   *  channels with no producer / no typed home (kept until their own epic). */
  s14Deletable: boolean;
};

/** The 9 documented typed-object side-channels — §3.1. The census gate asserts
 *  the registry covers EXACTLY this set (assertNever-style exhaustiveness). */
export const DOCUMENTED_TYPED_SIDE_CHANNELS = [
  "__entity_annotative_contexts",
  "__dimension_annotative_contexts",
  "__mleader_annotative_contexts",
  "__mleader_leader_roots",
  "__mleader_block_attrs",
  "__revcloud_vertices",
  "__hatch_boundary_paths",
  "__hatch_gradient",
  "__array_source_ids",
] as const;

export type DocumentedTypedSideChannel = (typeof DOCUMENTED_TYPED_SIDE_CHANNELS)[number];

export const SIDE_CHANNEL_REGISTRY: readonly SideChannelDescriptor[] = [
  // ── the 9 typed-object side-channels (§3.1) ──
  {
    key: "__entity_annotative_contexts",
    kind: "typed-object",
    typedHome: "annotativeContexts",
    // Block INSERT previews ALSO emit this for their annotativeContexts (P2 #9);
    // omitting Block would leave annotative blocks unaccounted for in S14's
    // by-construction deletion / S16 classification.
    carriers: ["Text", "MText", "Hatch", "Block"],
    s14Deletable: true,
  },
  {
    key: "__dimension_annotative_contexts",
    kind: "typed-object",
    typedHome: "annotativeContexts",
    carriers: ["Dimension"],
    s14Deletable: true,
  },
  {
    key: "__mleader_annotative_contexts",
    kind: "typed-object",
    typedHome: "annotativeContexts",
    carriers: ["Multileader"],
    s14Deletable: true,
  },
  {
    key: "__mleader_leader_roots",
    kind: "typed-object",
    typedHome: "leaderRoots",
    carriers: ["Multileader"],
    s14Deletable: true,
  },
  {
    key: "__mleader_block_attrs",
    kind: "typed-object",
    typedHome: "blockAttributeReferences",
    carriers: ["Multileader"],
    s14Deletable: true,
  },
  {
    key: "__revcloud_vertices",
    kind: "typed-object",
    typedHome: "vertices",
    carriers: ["Revision Cloud"],
    s14Deletable: true,
  },
  {
    key: "__hatch_boundary_paths",
    kind: "typed-object",
    typedHome: "boundaryPaths",
    carriers: ["Hatch"],
    s14Deletable: true,
  },
  {
    key: "__hatch_gradient",
    kind: "typed-object",
    typedHome: "gradient",
    carriers: ["Hatch"],
    s14Deletable: true,
  },
  {
    key: "__array_source_ids",
    kind: "typed-object",
    typedHome: "sourceEntityIds",
    carriers: ["Rectangular Associative Array"],
    s14Deletable: true,
  },
  // ── reader-only channels (§3.1) — no producer yet ──
  {
    key: "__viewport_clip_path",
    kind: "reader-only",
    typedHome: "clipPath",
    carriers: ["Viewport"],
    s14Deletable: false, // gated on EPIC-VIEWPORT-CLIP producer
  },
  {
    key: "__viewport_child_ids",
    kind: "reader-only",
    typedHome: "childEntityIds",
    carriers: ["Viewport"],
    s14Deletable: false,
  },
  {
    key: "__active_annotation_scale",
    kind: "reader-only",
    typedHome: "header.annotationScale",
    carriers: [],
    s14Deletable: false,
  },
  // ── Appendix-A orphans ──
  {
    key: "Rotation",
    kind: "orphan",
    typedHome: "", // dead — viewport frame has no rotation (group 51 = view twist)
    carriers: ["Viewport"],
    s14Deletable: true, // §9.4 — remove the key + the readNumber orphan read
  },
  {
    key: "MLVAR_*",
    kind: "orphan",
    typedHome: "extraMlVars",
    carriers: ["Multileader"],
    s14Deletable: false, // verify-in-S9 (produced-but-style-now-typed vs default)
  },
];

/** Every documented `__`-prefixed key the registry classifies. */
export const CLASSIFIED_SIDE_CHANNEL_KEYS: ReadonlySet<string> = new Set(
  SIDE_CHANNEL_REGISTRY.map((c) => c.key),
);
